/**
 * Settings — tenant configuration with sticky left sub-nav.
 * URL: /settings/{section}. Default redirects to /settings/general.
 *
 * Spec: user-uploads/LOVABLE_SETTINGS_PAGE.md
 */
import { Navigate, NavLink, useParams } from "react-router-dom";
import { SurfaceShell } from "@/components/surface/SurfaceShell";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { EnrollmentPanel } from "@/components/enrollment/EnrollmentPanel";
import { GeneralSection } from "@/components/settings/GeneralSection";
import { UsersRolesSection } from "@/components/settings/UsersRolesSection";
import { SsoSection } from "@/components/settings/SsoSection";
import { ApiTokensSection } from "@/components/settings/ApiTokensSection";
import { WebhooksSection } from "@/components/settings/WebhooksSection";
import { SiemSection } from "@/components/settings/SiemSection";
import { NotificationsSection } from "@/components/settings/NotificationsSection";
import { ExternalSourcesSection } from "@/components/settings/ExternalSourcesSection";
import { AuditLogSection } from "@/components/settings/AuditLogSection";
import { cn } from "@/lib/utils";

interface Section {
  slug: string;
  label: string;
  group: string;
  render: () => JSX.Element;
}

const SECTIONS: Section[] = [
  { slug: "general",          label: "General",          group: "Workspace",     render: () => <GeneralSection /> },
  { slug: "enrollment",       label: "Enrollment",       group: "Workspace",     render: () => <EnrollmentPanel /> },
  { slug: "users-roles",      label: "Users & Roles",    group: "Access",        render: () => <UsersRolesSection /> },
  { slug: "sso",              label: "SSO",              group: "Access",        render: () => <SsoSection /> },
  { slug: "api-tokens",       label: "API Tokens",       group: "Access",        render: () => <ApiTokensSection /> },
  { slug: "webhooks",         label: "Webhooks",         group: "Integrations",  render: () => <WebhooksSection /> },
  { slug: "siem-forwarding",  label: "SIEM Forwarding",  group: "Integrations",  render: () => <SiemSection /> },
  { slug: "notifications",    label: "Notifications",    group: "Integrations",  render: () => <NotificationsSection /> },
  { slug: "external-sources", label: "External Sources", group: "Integrations",  render: () => <ExternalSourcesSection /> },
  { slug: "audit-log",        label: "Audit Log",        group: "Compliance",    render: () => <AuditLogSection /> },
];

const SECTION_NEEDS_EDR: Record<string, boolean> = {
  enrollment: true,
  "audit-log": true,
};

export default function SettingsPage() {
  const { tab } = useParams();
  if (!tab) return <Navigate to="/settings/general" replace />;

  const section = SECTIONS.find((s) => s.slug === tab);
  if (!section) return <Navigate to="/settings/general" replace />;

  const groups = Array.from(new Set(SECTIONS.map((s) => s.group)));
  const needsGate = SECTION_NEEDS_EDR[section.slug];
  const body = section.render();

  return (
    <SurfaceShell
      overline="System · Settings"
      title={section.label}
      summary="Tenant identity, access, integrations and compliance. Tenant-wide configuration lives here."
    >
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="lg:sticky lg:top-4 self-start space-y-4">
          {groups.map((g) => (
            <div key={g}>
              <div className="px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5">{g}</div>
              <nav className="flex flex-col gap-0.5">
                {SECTIONS.filter((s) => s.group === g).map((s) => (
                  <NavLink
                    key={s.slug}
                    to={`/settings/${s.slug}`}
                    className={({ isActive }) =>
                      cn(
                        "px-2.5 py-1.5 rounded text-xs transition-colors",
                        isActive
                          ? "bg-muted text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                      )
                    }
                  >
                    {s.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}
        </aside>
        <div className="min-w-0">{needsGate ? <ConnectorGate connectorId="edr">{body}</ConnectorGate> : body}</div>
      </div>
    </SurfaceShell>
  );
}
